import dynamic from 'next/dynamic'
import React, { FC, useEffect, useState } from 'react'
import { BaseReactPlayerProps } from 'react-player/base'
import styled from 'styled-components'

import SVGPause from '@Assets/SVG/Pause'
import SVGPlay from '@Assets/SVG/Play'
import SVGReplay from '@Assets/SVG/Replay'
import { Loading } from '@Components/CMS/Video'
import { Asset } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'
import { buttonReset } from '@Globals/Style/mixins'

const VideoControls = styled.div(({ theme }) => `
  display: flex;
  position: absolute;
  right: ${theme.padding.mobile};
  bottom: ${theme.padding.mobile};

  &:first-child {
    margin-right: 5px;
  }
`)

const VideoControlButton = styled.button`
  ${buttonReset};
  // Set as "important" because there'd be parent styling overruling this otherwise.
  background: rgba(255, 255, 255, .5) !important;
  padding: 8px;
  z-index: 3;

  > svg {
    width: 24px;
    height: 24px;
    vertical-align: bottom;
  }
`

const VideoRoot = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;

  ${Loading} {
    &:after {
      width: 70px;
      height: 70px;
    }
  }
`

const VideoContainer = styled.div<{ isFullscreen: boolean }>`
  display: flex;
  position: relative;
  height: ${({ isFullscreen }) => (isFullscreen ? '100vw' : 'auto')};
  width: 100vw;

  ${up('mobileStyleThreshold')} {
    margin-top: 0;
  }
`

const ReactPlayer = dynamic(() => import('react-player/lazy'))

const wistiaConfiguration = {
  wistia: {
    options: {
      controlsVisibleOnLoad: false,
      endVideoBehavior: 'loop',
      fullscreenButton: false,
      muted: true,
      playButton: false,
      playbackRateControl: false,
      playbar: false,
      settingsControl: false,
      smallPlayButton: false,
      volume: 0,
      volumeControl: false,
      videoFoam: false,
      wmode: 'transparent',
    },
  },
}

export const VideoHorizontal: FC<{ asset?: Asset, isActive: boolean, isFullscreen: boolean }> = ({
  asset,
  isActive = false,
  isFullscreen = false,
}) => {
  const [playerEmbed, setPlayer] = useState<BaseReactPlayerProps>()
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!playerEmbed) {
      return
    }

    setIsPlaying(isActive)
  }, [isActive, playerEmbed])

  if (!asset) {
    return null
  }

  const toggle = (event) => {
    event.stopPropagation()
    setIsPlaying(prev => !prev)
  }

  const replay = (event) => {
    event.stopPropagation()
    const reactPlayer = playerEmbed?.player.player
    if (reactPlayer) {
      reactPlayer.player.time(0)
      reactPlayer.player.play()
    }
  }

  return (
    <VideoRoot>
      {isLoading && <Loading />}
      <VideoContainer
        css={{
          maxHeight: isFullscreen ? '100vh' : '100%',
          maxWidth: isFullscreen ? '100vh' : '100%',
          height: '100%',
          width: '100%',

          '> .wistia_embed': {
            '> .wistia_embed': {
              ...(isFullscreen && {
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
              }),
            },
          },
        }}
        isFullscreen={isFullscreen}
      >
        <ReactPlayer
          className="wistia_embed"
          config={wistiaConfiguration}
          controls={false}
          height="100%"
          onReady={player => setPlayer(player)}
          onStart={() => setIsLoading(false)}
          playing={isPlaying}
          previewTabIndex={-1}
          url={`https://fast.wistia.net/medias/${asset.url}`}
          width="100%"
        />
        {playerEmbed && (
          <VideoControls>
            <VideoControlButton onClick={toggle} tabIndex={-1}>
              {
                isPlaying
                  ? <SVGPause />
                  : <SVGPlay />
              }
            </VideoControlButton>
            <VideoControlButton
              onClick={replay}
              tabIndex={-1}
            >
              <SVGReplay />
            </VideoControlButton>
          </VideoControls>
        )}
      </VideoContainer>
    </VideoRoot>
  )
}

export const VideoVertical: FC<{ asset?: Asset }> = ({ asset }) => {
  if (!asset) {
    return null
  }

  return (
    <>
      <img
        alt="bonkers"
        height="78px"
        src={`${asset.url}?width=78&quality=60,90`}
        width="78px"
      />
      <div
        css={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          background: 'rgb(0,0,0,0.3)',
          padding: 4,

          span: {
            fontSize: '.7em',
            marginTop: '-1.5em',
          },

          svg: {
            height: '75%',
            marginBottom: 3,
          },

          'svg path, span': {
            color: '#fff',
          },
        }}
      >
        <svg
          aria-hidden="true"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 5.14v14l11-7l-11-7Z" fill="currentColor" />
        </svg>
        <span>VIDEO</span>
      </div>
    </>
  )
}
